/**
 * StaffScheduler Component
 * Admin component to schedule meetings with staff individually or as a team
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { 
  collection, 
  query, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp 
} from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Users,
  Calendar as CalendarIcon,
  Clock,
  Plus,
  Video,
  Phone,
  MapPin,
  Settings,
  Save,
  Trash2,
  Edit,
  Loader2,
  UserPlus,
  CheckCircle,
} from "lucide-react";

const translations = {
  en: {
    title: "Staff Scheduler",
    subtitle: "Manage staff availability and schedule meetings",
    staffMembers: "Staff Members",
    addStaff: "Add Staff Member",
    editStaff: "Edit Staff",
    scheduleMeeting: "Schedule Meeting",
    availability: "Availability Settings",
    upcomingMeetings: "Upcoming Meetings",
    name: "Name",
    email: "Email",
    role: "Role",
    phone: "Phone",
    selectStaff: "Select Staff",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    meetingTitle: "Meeting Title",
    description: "Description",
    meetingType: "Meeting Type",
    videoCall: "Video Call",
    phoneCall: "Phone Call",
    inPerson: "In Person",
    location: "Location",
    date: "Date",
    time: "Time",
    duration: "Duration",
    minutes: "minutes",
    schedule: "Schedule",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    individual: "Individual",
    team: "Team Meeting",
    loading: "Loading...",
    noStaff: "No staff members configured",
    addFirstStaff: "Add your first staff member",
    noMeetings: "No upcoming meetings",
    availabilityDesc: "Set weekly availability for each staff member",
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    enabled: "Enabled",
    startTime: "Start",
    endTime: "End",
    staffAdded: "Staff member added",
    staffUpdated: "Staff member updated",
    staffDeleted: "Staff member deleted",
    meetingScheduled: "Meeting scheduled successfully",
    selectDate: "Select a date",
    participantsSelected: "participants selected",
  },
  fr: {
    title: "Planificateur Staff",
    subtitle: "Gérez les disponibilités du staff et planifiez des réunions",
    staffMembers: "Membres du Staff",
    addStaff: "Ajouter un membre",
    editStaff: "Modifier",
    scheduleMeeting: "Planifier une réunion",
    availability: "Paramètres de disponibilité",
    upcomingMeetings: "Réunions à venir",
    name: "Nom",
    email: "Email",
    role: "Rôle",
    phone: "Téléphone",
    selectStaff: "Sélectionner le staff",
    selectAll: "Tout sélectionner",
    deselectAll: "Tout désélectionner",
    meetingTitle: "Titre de la réunion",
    description: "Description",
    meetingType: "Type de réunion",
    videoCall: "Appel vidéo",
    phoneCall: "Appel téléphonique",
    inPerson: "En personne",
    location: "Lieu",
    date: "Date",
    time: "Heure",
    duration: "Durée",
    minutes: "minutes",
    schedule: "Planifier",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    individual: "Individuel",
    team: "Réunion d'équipe",
    loading: "Chargement...",
    noStaff: "Aucun membre du staff configuré",
    addFirstStaff: "Ajoutez votre premier membre",
    noMeetings: "Aucune réunion à venir",
    availabilityDesc: "Définissez les disponibilités hebdomadaires pour chaque membre",
    monday: "Lundi",
    tuesday: "Mardi",
    wednesday: "Mercredi",
    thursday: "Jeudi",
    friday: "Vendredi",
    saturday: "Samedi",
    sunday: "Dimanche",
    enabled: "Activé",
    startTime: "Début",
    endTime: "Fin",
    staffAdded: "Membre ajouté",
    staffUpdated: "Membre mis à jour",
    staffDeleted: "Membre supprimé",
    meetingScheduled: "Réunion planifiée avec succès",
    selectDate: "Sélectionnez une date",
    participantsSelected: "participants sélectionnés",
  },
};

interface StaffMember {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
  availability: WeeklyAvailability;
}

interface WeeklyAvailability {
  timezone: string;
  schedule: DaySchedule[];
}

interface DaySchedule {
  day: string;
  enabled: boolean;
  slots: { start: string; end: string }[];
}

interface ScheduledMeeting {
  id: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  meetingType: "video" | "phone" | "in_person";
  location?: string;
  participants: string[];
  createdBy: string;
}

const DEFAULT_AVAILABILITY: WeeklyAvailability = {
  timezone: "America/Montreal",
  schedule: [
    { day: "monday", enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    { day: "tuesday", enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    { day: "wednesday", enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    { day: "thursday", enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    { day: "friday", enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    { day: "saturday", enabled: true, slots: [{ start: "10:00", end: "14:00" }] },
    { day: "sunday", enabled: false, slots: [] },
  ],
};

export function StaffScheduler() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("schedule");

  // Add/Edit Staff Dialog
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [staffForm, setStaffForm] = useState({
    name: "",
    email: "",
    role: "",
    phone: "",
  });

  // Schedule Meeting State
  const [selectedStaff, setSelectedStaff] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [meetingForm, setMeetingForm] = useState({
    title: "",
    description: "",
    meetingType: "video" as "video" | "phone" | "in_person",
    location: "",
    time: "09:00",
    duration: 30,
  });
  const [isScheduling, setIsScheduling] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load staff members
      const staffRef = collection(db, "staffAvailability");
      const staffSnapshot = await getDocs(staffRef);
      const staffData: StaffMember[] = staffSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as StaffMember[];
      setStaffMembers(staffData);

      // Load upcoming meetings
      const meetingsRef = collection(db, "staffMeetings");
      const meetingsSnapshot = await getDocs(meetingsRef);
      const meetingsData: ScheduledMeeting[] = meetingsSnapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            startTime: data.startTime?.toDate?.() || new Date(data.startTime),
            endTime: data.endTime?.toDate?.() || new Date(data.endTime),
          };
        })
        .filter((m) => m.startTime > new Date())
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime()) as ScheduledMeeting[];
      setMeetings(meetingsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStaff = async () => {
    if (!staffForm.name || !staffForm.email || !staffForm.role) return;

    try {
      const staffData = {
        ...staffForm,
        availability: DEFAULT_AVAILABILITY,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (editingStaff) {
        await updateDoc(doc(db, "staffAvailability", editingStaff.id), {
          ...staffForm,
          updatedAt: serverTimestamp(),
        });
        toast({ title: t.staffUpdated });
      } else {
        await addDoc(collection(db, "staffAvailability"), staffData);
        toast({ title: t.staffAdded });
      }

      setShowStaffDialog(false);
      setEditingStaff(null);
      setStaffForm({ name: "", email: "", role: "", phone: "" });
      await loadData();
    } catch (error) {
      console.error("Error saving staff:", error);
    }
  };

  const handleDeleteStaff = async (staffId: string) => {
    try {
      await deleteDoc(doc(db, "staffAvailability", staffId));
      toast({ title: t.staffDeleted });
      await loadData();
    } catch (error) {
      console.error("Error deleting staff:", error);
    }
  };

  const handleScheduleMeeting = async () => {
    if (!meetingForm.title || selectedStaff.length === 0) return;

    setIsScheduling(true);
    try {
      const startTime = new Date(selectedDate);
      const [hours, minutes] = meetingForm.time.split(":").map(Number);
      startTime.setHours(hours, minutes, 0, 0);

      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + meetingForm.duration);

      const meetingData = {
        title: meetingForm.title,
        description: meetingForm.description,
        meetingType: meetingForm.meetingType,
        location: meetingForm.location,
        startTime,
        endTime,
        participants: selectedStaff,
        createdBy: user?.email || "",
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "staffMeetings"), meetingData);
      
      toast({ title: t.meetingScheduled });
      setMeetingForm({
        title: "",
        description: "",
        meetingType: "video",
        location: "",
        time: "09:00",
        duration: 30,
      });
      setSelectedStaff([]);
      await loadData();
    } catch (error) {
      console.error("Error scheduling meeting:", error);
    } finally {
      setIsScheduling(false);
    }
  };

  const toggleStaffSelection = (staffId: string) => {
    setSelectedStaff((prev) =>
      prev.includes(staffId)
        ? prev.filter((id) => id !== staffId)
        : [...prev, staffId]
    );
  };

  const selectAllStaff = () => {
    setSelectedStaff(staffMembers.map((s) => s.id));
  };

  const deselectAllStaff = () => {
    setSelectedStaff([]);
  };

  const getDayName = (day: string) => {
    const dayMap: Record<string, keyof typeof t> = {
      monday: "monday",
      tuesday: "tuesday",
      wednesday: "wednesday",
      thursday: "thursday",
      friday: "friday",
      saturday: "saturday",
      sunday: "sunday",
    };
    return t[dayMap[day] || "monday"];
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">{t.loading}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-500" />
              {t.title}
            </CardTitle>
            <CardDescription>{t.subtitle}</CardDescription>
          </div>
          <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingStaff(null);
                  setStaffForm({ name: "", email: "", role: "", phone: "" });
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t.addStaff}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingStaff ? t.editStaff : t.addStaff}</DialogTitle>
                <DialogDescription>
                  {language === "fr"
                    ? "Ajoutez un membre du staff avec ses informations"
                    : "Add a staff member with their information"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{t.name} *</Label>
                  <Input
                    value={staffForm.name}
                    onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.email} *</Label>
                  <Input
                    type="email"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                    placeholder="jean@acquizgroup.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.role} *</Label>
                  <Input
                    value={staffForm.role}
                    onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                    placeholder="Senior Advisor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.phone}</Label>
                  <Input
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                    placeholder="+1 514 555-1234"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowStaffDialog(false)}>
                    {t.cancel}
                  </Button>
                  <Button onClick={handleAddStaff}>
                    <Save className="h-4 w-4 mr-2" />
                    {t.save}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="schedule" className="gap-2">
              <CalendarIcon className="h-4 w-4" />
              {t.scheduleMeeting}
            </TabsTrigger>
            <TabsTrigger value="staff" className="gap-2">
              <Users className="h-4 w-4" />
              {t.staffMembers}
            </TabsTrigger>
            <TabsTrigger value="meetings" className="gap-2">
              <Clock className="h-4 w-4" />
              {t.upcomingMeetings}
            </TabsTrigger>
          </TabsList>

          {/* Schedule Meeting Tab */}
          <TabsContent value="schedule" className="space-y-6">
            {staffMembers.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">{t.noStaff}</p>
                <Button className="mt-4" onClick={() => setShowStaffDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t.addFirstStaff}
                </Button>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Staff Selection */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">{t.selectStaff}</Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={selectAllStaff}>
                        {t.selectAll}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={deselectAllStaff}>
                        {t.deselectAll}
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="h-[200px] border rounded-lg p-3">
                    <div className="space-y-2">
                      {staffMembers.map((staff) => (
                        <div
                          key={staff.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedStaff.includes(staff.id)
                              ? "bg-primary/10 border border-primary"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => toggleStaffSelection(staff.id)}
                        >
                          <Checkbox checked={selectedStaff.includes(staff.id)} />
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs">
                              {staff.name.split(" ").map((n) => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{staff.name}</p>
                            <p className="text-xs text-muted-foreground">{staff.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  {selectedStaff.length > 0 && (
                    <Badge variant="secondary">
                      {selectedStaff.length} {t.participantsSelected}
                    </Badge>
                  )}

                  {/* Meeting Details */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="space-y-2">
                      <Label>{t.meetingTitle} *</Label>
                      <Input
                        value={meetingForm.title}
                        onChange={(e) =>
                          setMeetingForm({ ...meetingForm, title: e.target.value })
                        }
                        placeholder={language === "fr" ? "Réunion d'équipe" : "Team Meeting"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.description}</Label>
                      <Textarea
                        value={meetingForm.description}
                        onChange={(e) =>
                          setMeetingForm({ ...meetingForm, description: e.target.value })
                        }
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t.meetingType}</Label>
                        <Select
                          value={meetingForm.meetingType}
                          onValueChange={(v) =>
                            setMeetingForm({
                              ...meetingForm,
                              meetingType: v as "video" | "phone" | "in_person",
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="video">
                              <div className="flex items-center gap-2">
                                <Video className="h-4 w-4" />
                                {t.videoCall}
                              </div>
                            </SelectItem>
                            <SelectItem value="phone">
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                {t.phoneCall}
                              </div>
                            </SelectItem>
                            <SelectItem value="in_person">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {t.inPerson}
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t.duration}</Label>
                        <Select
                          value={String(meetingForm.duration)}
                          onValueChange={(v) =>
                            setMeetingForm({ ...meetingForm, duration: Number(v) })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 {t.minutes}</SelectItem>
                            <SelectItem value="30">30 {t.minutes}</SelectItem>
                            <SelectItem value="45">45 {t.minutes}</SelectItem>
                            <SelectItem value="60">60 {t.minutes}</SelectItem>
                            <SelectItem value="90">90 {t.minutes}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calendar & Time */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold">{t.selectDate}</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-md border"
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                  <div className="space-y-2">
                    <Label>{t.time}</Label>
                    <Input
                      type="time"
                      value={meetingForm.time}
                      onChange={(e) =>
                        setMeetingForm({ ...meetingForm, time: e.target.value })
                      }
                    />
                  </div>

                  <Button
                    className="w-full"
                    onClick={handleScheduleMeeting}
                    disabled={isScheduling || selectedStaff.length === 0 || !meetingForm.title}
                  >
                    {isScheduling ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CalendarIcon className="h-4 w-4 mr-2" />
                    )}
                    {t.schedule}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Staff Members Tab */}
          <TabsContent value="staff" className="space-y-4">
            {staffMembers.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">{t.noStaff}</p>
                <Button className="mt-4" onClick={() => setShowStaffDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t.addFirstStaff}
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {staffMembers.map((staff) => (
                  <Card key={staff.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>
                            {staff.name.split(" ").map((n) => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h4 className="font-semibold">{staff.name}</h4>
                          <p className="text-sm text-muted-foreground">{staff.role}</p>
                          <p className="text-sm text-muted-foreground">{staff.email}</p>
                          {staff.phone && (
                            <p className="text-sm text-muted-foreground">{staff.phone}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setEditingStaff(staff);
                            setStaffForm({
                              name: staff.name,
                              email: staff.email,
                              role: staff.role,
                              phone: staff.phone || "",
                            });
                            setShowStaffDialog(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t.editStaff}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteStaff(staff.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Upcoming Meetings Tab */}
          <TabsContent value="meetings" className="space-y-4">
            {meetings.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">{t.noMeetings}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {meetings.map((meeting) => (
                  <Card key={meeting.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            {meeting.meetingType === "video" && <Video className="h-6 w-6 text-primary" />}
                            {meeting.meetingType === "phone" && <Phone className="h-6 w-6 text-primary" />}
                            {meeting.meetingType === "in_person" && <MapPin className="h-6 w-6 text-primary" />}
                          </div>
                          <div>
                            <h4 className="font-semibold">{meeting.title}</h4>
                            <p className="text-sm text-muted-foreground">
                              {meeting.startTime.toLocaleDateString(language === "fr" ? "fr-CA" : "en-US", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                              })}{" "}
                              •{" "}
                              {meeting.startTime.toLocaleTimeString(language === "fr" ? "fr-CA" : "en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary">
                          {meeting.participants.length} {t.participantsSelected}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
